import { DbEngine, type DbTarget } from '@bartizo/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { Button } from '../ui/Button.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * The databases a layer monitors (knowledge doc section 8.4): `db_size.targets`,
 * which `db_health` and `db_exposure` read as well.
 *
 * Structured fields, one set per database, because section 11 rules out raw config
 * text and a target is more than a line: an engine, where to reach it, and the
 * name of the secret file on the server that holds its password — never the
 * password, which does not leave the server (rule 5).
 *
 * On a server's own settings it also offers what that server's installer
 * prepared: `install.sh` records each monitoring account it creates, the agent
 * reports the record, and adding one here is a click rather than retyping what the
 * installer already knew. It is still a choice somebody makes, because targets are
 * settings and settings come from the API.
 *
 * Bounds are shown, not enforced: `SettingsPatch` at the API is what refuses a bad
 * target, with a reason.
 */

const ENGINE_LABELS: Record<DbTarget['engine'], string> = {
  postgres: 'PostgreSQL',
  mysql: 'MySQL or MariaDB',
  redis: 'Redis or Valkey',
  mongodb: 'MongoDB',
};

const GB = 1_000_000_000;

export interface TargetListEditorProps {
  value: DbTarget[];
  onChange: (next: DbTarget[]) => void;
  disabled: boolean;
  /** What this server's installer prepared, when editing a server's own layer. */
  detected?: readonly DbTarget[] | undefined;
}

/** A field left empty is not set, rather than set to an empty string. */
function withField<K extends keyof DbTarget>(
  target: DbTarget,
  key: K,
  value: DbTarget[K] | undefined,
): DbTarget {
  const empty = value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  const rest = Object.fromEntries(Object.entries(target).filter(([name]) => name !== key));
  return (empty ? rest : { ...rest, [key]: value }) as DbTarget;
}

const describe = (target: DbTarget): string => {
  const where =
    target.socket ??
    (target.host === undefined && target.port === undefined
      ? 'this machine'
      : `${target.host ?? 'this machine'}${target.port === undefined ? '' : `:${String(target.port)}`}`);
  return `${ENGINE_LABELS[target.engine]} at ${where}${target.secret_ref === undefined ? '' : `, secret ${target.secret_ref}`}`;
};

export function TargetListEditor({
  value,
  onChange,
  disabled,
  detected = [],
}: TargetListEditorProps): ReactNode {
  const replace = (index: number, next: DbTarget): void => {
    onChange(value.map((target, i) => (i === index ? next : target)));
  };
  const offered = detected.filter((found) => !value.some((target) => target.name === found.name));

  return (
    <div className="target-list">
      {value.length === 0 && <p className="muted text-sm">No databases set in this layer.</p>}

      {value.map((target, index) => {
        const title = target.name === '' ? `Database ${String(index + 1)}` : target.name;
        const set = <K extends keyof DbTarget>(key: K, field: DbTarget[K] | undefined): void => {
          replace(index, withField(target, key, field));
        };
        return (
          <fieldset key={index} className="target" disabled={disabled}>
            <legend>{title}</legend>
            <div className="target-grid">
              <TextField
                label="Name"
                hint="Unique on this server; labels its metrics and events."
                value={target.name}
                maxLength={64}
                onChange={(event) => {
                  replace(index, { ...target, name: event.target.value });
                }}
              />
              <SelectField
                label="Engine"
                value={target.engine}
                onChange={(event) => {
                  const engine = DbEngine.safeParse(event.target.value);
                  if (engine.success) replace(index, { ...target, engine: engine.data });
                }}
              >
                {DbEngine.options.map((engine) => (
                  <option key={engine} value={engine}>
                    {ENGINE_LABELS[engine]}
                  </option>
                ))}
              </SelectField>
              <TextField
                label="Host"
                hint="Empty means this machine. A remote database goes in the secret file as a URL instead."
                value={target.host ?? ''}
                onChange={(event) => {
                  set('host', event.target.value.trim());
                }}
              />
              <TextField
                label="Port"
                type="number"
                min={1}
                max={65535}
                value={target.port === undefined ? '' : String(target.port)}
                onChange={(event) => {
                  const port = Number(event.target.value);
                  set(
                    'port',
                    event.target.value === '' || !Number.isInteger(port) ? undefined : port,
                  );
                }}
              />
              <TextField
                label="Socket"
                hint="A Unix socket path, instead of a host."
                value={target.socket ?? ''}
                onChange={(event) => {
                  set('socket', event.target.value.trim());
                }}
              />
              <TextField
                label="Account"
                hint="Empty means bartizo_ro, which the installer creates."
                value={target.user ?? ''}
                onChange={(event) => {
                  set('user', event.target.value.trim());
                }}
              />
              <TextField
                label="Secret"
                hint="The name of a file in /etc/bartizo/secrets on the server, without .secret. Never the password."
                value={target.secret_ref ?? ''}
                onChange={(event) => {
                  set('secret_ref', event.target.value.trim());
                }}
              />
              <CommaListField
                label="Only these databases"
                hint="Comma-separated. Empty means every one the account can see."
                value={target.databases ?? []}
                onChange={(names) => {
                  set('databases', names);
                }}
              />
              <TextField
                label="Alert above"
                hint="In GB, for the whole instance: disk, or memory for Redis. Empty means no limit."
                type="number"
                min={0.001}
                step="any"
                value={
                  target.size_limit_bytes === undefined ? '' : String(target.size_limit_bytes / GB)
                }
                onChange={(event) => {
                  const gb = Number(event.target.value);
                  set(
                    'size_limit_bytes',
                    event.target.value === '' || !Number.isFinite(gb)
                      ? undefined
                      : Math.round(gb * GB),
                  );
                }}
              />
            </div>
            <div className="row">
              <Button
                variant="ghost"
                onClick={() => {
                  onChange(value.filter((_, i) => i !== index));
                }}
              >
                Remove {title}
              </Button>
            </div>
          </fieldset>
        );
      })}

      <div className="row">
        <Button
          disabled={disabled}
          onClick={() => {
            onChange([...value, { name: '', engine: 'postgres' }]);
          }}
        >
          Add a database
        </Button>
      </div>

      {offered.length > 0 && (
        <div className="target-offers" role="group" aria-label="Prepared by the installer">
          <p className="text-sm">
            The installer on this server prepared a monitoring account for{' '}
            {offered.length === 1 ? 'this database' : 'these databases'}:
          </p>
          <ul className="plain-list">
            {offered.map((found) => (
              <li key={found.name} className="row">
                <span>
                  <strong>{found.name}</strong>
                  <span className="cell-sub">{describe(found)}</span>
                </span>
                <Button
                  disabled={disabled}
                  onClick={() => {
                    onChange([...value, { ...found }]);
                  }}
                >
                  Add {found.name}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * A comma-separated list, with its draft held here: parsing on every keystroke
 * would drop the comma somebody just typed before they typed what follows it.
 */
function CommaListField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
}): ReactNode {
  const joined = value.join(', ');
  const [draft, setDraft] = useState(joined);
  useEffect(() => {
    setDraft((current) => (parse(current).join(', ') === joined ? current : joined));
  }, [joined]);
  return (
    <TextField
      label={label}
      hint={hint}
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(parse(event.target.value));
      }}
    />
  );
}

const parse = (text: string): string[] =>
  text
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name !== '');
